import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { guestPlayerService, type LessonState, type LessonActivity } from '@/services/guestPlayerService';
import { BlocklyActivity } from '@/components/blockly/BlocklyActivity';
import { ShellActivity } from '@/components/player/ShellActivity';
import { PhishingQuizActivity } from '@/components/player/PhishingQuizActivity';
import { PacketAnalysisActivity } from '@/components/player/PacketAnalysisActivity';
import { CyberHygieneQuizActivity } from '@/components/player/CyberHygieneQuizActivity';
import { CodingQuizActivity } from '@/components/player/CodingQuizActivity';
import { PythonReplActivity } from '@/components/player/PythonReplActivity';
import { McqActivity } from '@/components/player/McqActivity';
import { BreachCheckActivity } from '@/components/player/BreachCheckActivity';
import { PasswordCheckActivity } from '@/components/player/PasswordCheckActivity';
import { PotatoPiratesActivity } from '@/components/potatoPirates/PotatoPiratesActivity';
import { SelfPacedPlayerView } from '@/components/player/SelfPacedPlayerView';
import { LessonResults } from '@/components/player/LessonResults';
import styles from './GuestPlay.module.css';

export function GuestPlay() {
  const { runCode } = useParams<{ runCode: string }>();
  const navigate = useNavigate();

  const [lessonState, setLessonState] = useState<LessonState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [activityStats, setActivityStats] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isActivityPaused, setIsActivityPaused] = useState(false);
  const [completedActivity, setCompletedActivity] = useState<LessonActivity | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const playerIdRef = useRef<string | null>(null);

  const loadLessonState = useCallback(async () => {
    if (!runCode) return;

    const result = await guestPlayerService.getLessonState(runCode);

    if (result.success && result.data) {
      // If the new state has a new active activity, clear the completed one
      if (result.data.current_activity?.status === 'active') {
        setCompletedActivity(null);
      }
      // If current_activity just disappeared, preserve it so all players see the correct answer
      setLessonState(prev => {
        if (prev?.current_activity && !result.data!.current_activity) {
          setCompletedActivity(prev.current_activity);
        }
        return result.data!;
      });
      setIsPaused(result.data.lesson.status === 'paused');
      setError(null);
    } else {
      const errorMessage = result.error || 'Failed to load lesson state';
      setError(errorMessage);

      if (errorMessage.includes('Session invalid') || errorMessage.includes('not found')) {
        const recovery = await guestPlayerService.recoverSession();

        if (recovery.success) {
          const retryResult = await guestPlayerService.getLessonState(runCode);
          if (retryResult.success && retryResult.data) {
            setLessonState(retryResult.data);
            setError(null);
          } else {
            navigate(`/join/${runCode}`);
          }
        } else {
          navigate(`/join/${runCode}`);
        }
      }
    }

    setIsLoading(false);
  }, [runCode, navigate]);

  const initializeLesson = useCallback(async () => {
    if (!runCode) return;

    setIsLoading(true);

    const recovery = await guestPlayerService.recoverSession();

    if (recovery.success && recovery.runCode === runCode) {
      await loadLessonState();
    } else {
      await loadLessonState();
    }
  }, [runCode, loadLessonState]);

  const wsCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!runCode) {
      navigate('/auth');
      return;
    }

    initializeLesson();
    const wsCleanup = setupWebSocketConnection();
    wsCleanupRef.current = wsCleanup || null;
    pollingIntervalRef.current = setInterval(loadLessonState, 30000);

    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      wsCleanupRef.current?.();
      cleanupWebSocket();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runCode]);

  useEffect(() => {
    // Poll faster during draft or paused states so UI updates quickly on resume/start
    if ((lessonState?.lesson.status === 'draft' || lessonState?.lesson.status === 'paused') && !wsConnected) {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = setInterval(loadLessonState, 3000);
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [lessonState?.lesson.status, wsConnected, loadLessonState]);

  useEffect(() => {
    if (lessonState?.lesson.status === 'completed') {
      // Give players time to read the end-of-lesson leaderboard and recap
      // before the session is auto-cleaned.
      const autoCleanupTimer = setTimeout(() => {
        guestPlayerService.clearSession();
        navigate('/join');
      }, 180000);
      return () => clearTimeout(autoCleanupTimer);
    }
  }, [lessonState?.lesson.status, navigate]);

  // Keep playerIdRef in sync for use in WebSocket closures
  useEffect(() => {
    playerIdRef.current = lessonState?.player?.id ?? null;
  }, [lessonState?.player?.id]);

  const SLIDE_TYPES = ['pdf_slideshow', 'google_slides']

  const startActivityTimer = useCallback((activity: any) => {
    if (!activity.time_limit && !activity.time_limit_seconds) return;
    // Don't show timer for slide activities
    const actType = activity.type || activity.activity_type
    if (actType && SLIDE_TYPES.includes(actType)) return;
    const timeLimitSeconds = activity.time_limit_seconds || activity.time_limit;
    const startedAt = activity.started_at ? new Date(activity.started_at) : new Date();
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
      const remaining = Math.max(0, timeLimitSeconds - elapsed);
      setTimeRemaining(remaining);
      if (remaining === 0) {
        clearInterval(timerIntervalRef.current!);
        timerIntervalRef.current = null;
        setTimedOut(true);
      }
    }, 1000);
  }, []);

  const stopActivityTimer = useCallback((preserveTime = false) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (!preserveTime) {
      setTimeRemaining(null);
    }
  }, []);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const setupWebSocketConnection = useCallback(() => {
    if (!runCode) return;
    guestPlayerService.connectWebSocket(runCode);

    const handleWebSocketConnected = () => {
      setWsConnected(true);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    const handleWebSocketDisconnected = () => {
      setWsConnected(false);
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(loadLessonState, 10000);
      }
    };

    const handleLessonStateUpdate = (newLessonState: LessonState) => {
      setLessonState(newLessonState);
      setError(null);
      setIsPaused(newLessonState.lesson.status === 'paused');
      if (newLessonState.current_activity?.status === 'active') {
        setTimedOut(false);
        setIsActivityPaused(false);
        setCompletedActivity(null);
        startActivityTimer(newLessonState.current_activity);
      } else if (newLessonState.current_activity?.status === 'paused') {
        setIsActivityPaused(true);
        stopActivityTimer(true);
      } else {
        setIsActivityPaused(false);
        stopActivityTimer();
      }
    };

    const handleActivityStarted = (data: any) => {
      setTimedOut(false);
      setCompletedActivity(null);
      loadLessonState();
      if (data.time_limit_seconds || data.time_limit) {
        startActivityTimer(data);
      }
    };

    const handlePlayerResponded = (data: any) => {
      setActivityStats(data);
    };

    const handleActivityComplete = (_data: any) => {
    };

    const handleActivityCompleted = () => {
      stopActivityTimer();
      setIsActivityPaused(false);
      // Preserve the current activity so all players can see the correct answer
      setLessonState(prev => {
        if (prev?.current_activity) {
          setCompletedActivity(prev.current_activity);
        }
        return prev;
      });
      loadLessonState();
    };

    const handleWebSocketError = (_data: any) => {
      setError('Connection error. Please refresh the page.');
    };

    const handleWebSocketRejected = () => {
      setError('Session expired. Redirecting to join page...');
      setTimeout(() => navigate(`/join/${runCode}`), 2000);
    };

    const handlePaused = (level: 'lesson' | 'activity') => {
      if (level === 'lesson') setIsPaused(true);
      else setIsActivityPaused(true);
      stopActivityTimer(true);
    };

    const handleResumed = (level: 'lesson' | 'activity', data?: any) => {
      if (level === 'lesson') setIsPaused(false);
      else setIsActivityPaused(false);
      loadLessonState();
      // On activity resume, restart timer with updated started_at from broadcast data
      if (level === 'activity' && (data?.time_limit_seconds || data?.time_limit)) {
        startActivityTimer(data);
      }
    };

    const handlePlayerKicked = (_data: any) => {
    };

    const handlePlayerKickedSelf = (data: any) => {
      setError(`You have been removed from the lesson. ${data.reason ? `Reason: ${data.reason}` : ''}`);
      setTimeout(() => {
        guestPlayerService.clearSession();
        navigate(`/join/${runCode}`);
      }, 3000);
    };

    guestPlayerService.on('websocket_connected', handleWebSocketConnected);
    guestPlayerService.on('websocket_disconnected', handleWebSocketDisconnected);
    guestPlayerService.on('lesson_state_changed', handleLessonStateUpdate);
    guestPlayerService.on('activity_started', handleActivityStarted);
    guestPlayerService.on('player_responded', handlePlayerResponded);
    guestPlayerService.on('activity_complete', handleActivityComplete);
    guestPlayerService.on('activity_completed', handleActivityCompleted);
    guestPlayerService.on('websocket_error', handleWebSocketError);
    guestPlayerService.on('websocket_rejected', handleWebSocketRejected);
    guestPlayerService.on('player_kicked', handlePlayerKicked);
    guestPlayerService.on('player_kicked_self', handlePlayerKickedSelf);

    const handleLessonCompleted = () => {
      stopActivityTimer();
      setIsActivityPaused(false);
      setIsPaused(false);
      setCompletedActivity(null);
      setLessonState(prev => prev ? {
        ...prev,
        lesson: { ...prev.lesson, status: 'completed' },
        current_activity: null
      } : null);
    };

    const handleLessonPaused = () => handlePaused('lesson');
    const handleLessonResumed = () => handleResumed('lesson');
    const handleActivityPaused = () => handlePaused('activity');
    const handleActivityResumed = (data: any) => handleResumed('activity', data);

    const handleActivityTimeUpdated = (data: any) => {
      if (data.time_limit_seconds || data.time_limit) {
        setTimedOut(false);
        startActivityTimer(data);
      }
    };

    guestPlayerService.on('lesson_completed', handleLessonCompleted);
    guestPlayerService.on('lesson_paused', handleLessonPaused);
    guestPlayerService.on('lesson_resumed', handleLessonResumed);
    guestPlayerService.on('activity_paused', handleActivityPaused);
    guestPlayerService.on('activity_resumed', handleActivityResumed);
    guestPlayerService.on('activity_time_updated', handleActivityTimeUpdated);

    return () => {
      guestPlayerService.off('websocket_connected', handleWebSocketConnected);
      guestPlayerService.off('websocket_disconnected', handleWebSocketDisconnected);
      guestPlayerService.off('lesson_state_changed', handleLessonStateUpdate);
      guestPlayerService.off('activity_started', handleActivityStarted);
      guestPlayerService.off('player_responded', handlePlayerResponded);
      guestPlayerService.off('activity_complete', handleActivityComplete);
      guestPlayerService.off('activity_completed', handleActivityCompleted);
      guestPlayerService.off('websocket_error', handleWebSocketError);
      guestPlayerService.off('websocket_rejected', handleWebSocketRejected);
      guestPlayerService.off('player_kicked', handlePlayerKicked);
      guestPlayerService.off('player_kicked_self', handlePlayerKickedSelf);
      guestPlayerService.off('lesson_completed', handleLessonCompleted);
      guestPlayerService.off('lesson_paused', handleLessonPaused);
      guestPlayerService.off('lesson_resumed', handleLessonResumed);
      guestPlayerService.off('activity_paused', handleActivityPaused);
      guestPlayerService.off('activity_resumed', handleActivityResumed);
      guestPlayerService.off('activity_time_updated', handleActivityTimeUpdated);
    };
  }, [runCode, navigate, loadLessonState, lessonState?.player?.id]);

  const cleanupWebSocket = useCallback(() => {
    guestPlayerService.disconnectWebSocket();
  }, []);

  const handleResponseChange = (field: string, value: any) => {
    setResponseData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitResponse = async () => {
    if (!runCode || !lessonState?.current_activity) return;
    setIsSubmitting(true);

    const result = await guestPlayerService.submitResponse(
      runCode,
      lessonState.current_activity.id,
      responseData
    );

    if (result.success) {
      setResponseData({});
      await loadLessonState();
    } else {
      const err = result.error || 'Failed to submit response';
      setError(err);
    }

    setIsSubmitting(false);
  };

  const renderActivityContent = (activity: LessonActivity, opts?: { revealAnswers?: boolean }) => {
    // WebSocket payloads may send activity_type instead of type — normalise before switching.
    const actType = activity.type || (activity as any).activity_type
    switch (actType) {
      case 'mcq':
        return (
          <McqActivity
            activityData={activity.activity_data}
            description={activity.description}
            disabled={activity.player_has_responded}
            revealAnswer={opts?.revealAnswers}
            onSubmitResponse={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              if (result.success) await loadLessonState()
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'coding':
        return (
          <BlocklyActivity
            activity={activity}
            disabled={activity.player_has_responded}
            onSubmit={async (data) => {
              setIsSubmitting(true);
              const result = await guestPlayerService.submitResponse(
                runCode!,
                activity.id,
                data
              );
              if (result.success) {
                await loadLessonState();
              } else {
                const err = result.error || 'Failed to submit response';
                setError(err);
              }
              setIsSubmitting(false);
            }}
          />
        );

      case 'command_shell':
        return (
          <ShellActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            onExecuteCommand={async (cmd) => {
              return await guestPlayerService.executeShellCommand(runCode!, activity.id, cmd)
            }}
            onSubmitAnswer={async (answer) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, { answer })
              if (result.success) await loadLessonState()
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'phishing_quiz':
        return (
          <PhishingQuizActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            onSubmitQuiz={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              if (result.success) await loadLessonState()
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'packet_analysis':
        return (
          <PacketAnalysisActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            onSubmitQuiz={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              if (result.success) await loadLessonState()
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'cyber_hygiene_quiz':
        return (
          <CyberHygieneQuizActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            onSubmitQuiz={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              if (result.success) await loadLessonState()
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'coding_quiz':
        return (
          <CodingQuizActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            onSubmitQuiz={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              if (result.success) await loadLessonState()
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'python_repl':
        return (
          <PythonReplActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            onSubmitResponse={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              if (result.success) await loadLessonState()
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'breach_check':
        return (
          <BreachCheckActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            runCode={runCode!}
            // Record participation but don't reload lesson state: reloading would
            // flip player_has_responded → disabled and lock the input, whereas
            // this activity is meant to let players keep checking more emails.
            // The host still drives progression via WebSocket events.
            onSubmitResponse={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'password_check':
        return (
          <PasswordCheckActivity
            activityData={activity.activity_data}
            disabled={activity.player_has_responded}
            runCode={runCode!}
            // See breach_check above: keep the activity interactive for repeated
            // checks instead of locking it after the first submission.
            onSubmitResponse={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              return { success: result.success, error: result.error }
            }}
          />
        );

      case 'potato_pirates':
        return (
          <PotatoPiratesActivity
            key={activity.id}
            activity={activity}
            disabled={activity.player_has_responded}
            onSubmit={async (data) => {
              const result = await guestPlayerService.submitResponse(runCode!, activity.id, data)
              if (result.success) await loadLessonState()
            }}
          />
        );

      case 'pdf_slideshow':
      case 'google_slides':
      case 'youtube_video':
        return (
          <div className={styles.unsupportedCenter}>
            <svg className={styles.slidesIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className={styles.unsupportedText}>Look to the captain's screen</p>
            <p className={styles.unsupportedSub}>Follow along as your instructor presents.</p>
          </div>
        );

      default:
        return (
          <div className={styles.unsupportedCenter}>
            <p className={styles.unsupportedText}>Activity type "{actType}" not yet supported in guest mode.</p>
            <p className={styles.unsupportedSub}>Please check with the captain.</p>
          </div>
        );
    }
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className={styles.screenCenter}>
        <div className={styles.stateCard}>
          <div className={styles.skeleton}>
            <div className={styles.skeletonCircle}></div>
            <div className={styles.skeletonLine}></div>
            <div className={styles.skeletonLine}></div>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className={styles.screenCenter}>
        <div className={styles.stateCard}>
          <div className={styles.errorIconWrap}>
            <svg className={styles.errorIconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className={styles.errorTitle}>Connection Error</h2>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={loadLessonState} className={styles.btnPrimary}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── No data ──
  if (!lessonState) {
    return (
      <div className={styles.screenCenter}>
        <p className={styles.description}>No lesson data available.</p>
      </div>
    );
  }

  // ── Draft / Lobby ──
  if (lessonState.lesson.status === 'draft') {
    const displayName = lessonState.player.display_name;
    const avatarLetter = displayName.charAt(0).toUpperCase();

    return (
      <div className={styles.lobbyPage}>
        <div className={styles.lobbyTopLeft}>
          <div className={styles.lobbyChip}>
            <svg className={styles.lobbyChipIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className={styles.lobbyChipText}>{lessonState.lesson.current_players} joined</span>
          </div>
        </div>

        <div className={styles.lobbyTopRight}>
          <div className={styles.lobbyCodeBox}>
            <p className={styles.lobbyCodeLabel}>Lesson Code</p>
            <p className={styles.lobbyCodeValue}>{runCode}</p>
          </div>
        </div>

        <div className={styles.lobbyCenter}>
          <div className={styles.lobbyAvatarWrap}>
            <div className={styles.lobbyPing} />
            <div className={styles.lobbyPing2} />
            <div className={styles.lobbyAvatar}>
              <span className={styles.lobbyAvatarLetter}>{avatarLetter}</span>
            </div>
          </div>

          <p className={styles.lobbyJoinedAs}>Joined as</p>
          <h2 className={styles.lobbyDisplayName}>{displayName}</h2>

          <div className={styles.lobbyLessonBox}>
            <p className={styles.lobbyLessonLabel}>Lesson</p>
            <p className={styles.lobbyLessonName}>{lessonState.lesson.name}</p>
          </div>

          <div className={styles.lobbyDots}>
            <div className={styles.lobbyDot} />
            <div className={styles.lobbyDot} />
            <div className={styles.lobbyDot} />
          </div>
          <p className={styles.lobbyWaitText}>Waiting for captain to start...</p>
        </div>
      </div>
    );
  }

  // ── Self-paced (accessible even when completed) ──
  const isSelfPacedLesson = (lessonState.lesson as any).mode === 'self_paced'
  if (isSelfPacedLesson && (lessonState.lesson.status === 'live' || lessonState.lesson.status === 'completed')) {
    return (
      <SelfPacedPlayerView
        runCode={runCode!}
        lessonName={lessonState.lesson.name}
        playerName={lessonState.player.display_name}
        sessionValid={lessonState.session_valid}
      />
    );
  }

  // ── Completed ──
  if (lessonState.lesson.status === 'completed') {
    return (
      <LessonResults
        fetchResults={() => guestPlayerService.getLessonResults(runCode!)}
        playerId={lessonState.player.id}
        onExit={() => {
          guestPlayerService.clearSession();
          navigate('/join');
        }}
      />
    );
  }

  // (Self-paced check moved above the completed check)

  // ── Live play ──
  return (
    <div className={styles.liveContainer}>
      {/* Main Content */}
      <div className={styles.liveContent}>
        <div className={styles.liveCard}>
          {/* Status bar */}
          <div className={styles.statusBar}>
            <div className={styles.statusBarLeft}>
              <div className={`${styles.connDot} ${wsConnected ? styles.connDotLive : styles.connDotPoll}`}></div>
              <span className={styles.connLabel}>
                {wsConnected ? 'Live' : 'Polling'}
              </span>
            </div>
            <div className={styles.statusBarRight}>
              {lessonState.player.total_score && (
                <div className={styles.statBlock}>
                  <p className={styles.statBlockLabel}>Score</p>
                  <p className={styles.statBlockValue}>{lessonState.player.total_score}</p>
                </div>
              )}

              {timeRemaining !== null && (
                <div className={styles.statBlock}>
                  <p className={styles.statBlockLabel}>Time Left</p>
                  <p className={`${styles.statBlockValue} ${timeRemaining <= 60 ? styles.statBlockDanger : ''}`}>
                    {formatTime(timeRemaining)}
                  </p>
                </div>
              )}

              {activityStats && (
                <div className={styles.statBlock}>
                  <p className={styles.statBlockLabel}>Responses</p>
                  <p className={styles.statBlockValue}>
                    {activityStats.response_count}/{activityStats.total_players}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Activity */}
          <div className={styles.activityArea}>
            {lessonState.current_activity && !timedOut ? (
              renderActivityContent(lessonState.current_activity)
            ) : completedActivity ? (
              renderActivityContent(completedActivity, { revealAnswers: true })
            ) : (
              <div className={styles.waitingCenter}>
                <div className={styles.waitingIconWrap}>
                  <svg className={styles.waitingIconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className={styles.waitingTitle}>Waiting for Next Activity</h3>
                <p className={styles.waitingText}>Your captain will start the next activity soon.</p>
                {lessonState.lesson.status === 'draft' && (
                  <p className={styles.waitingSubtext}>The lesson hasn't started yet. Please wait for your captain.</p>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Paused overlay — lesson or activity level */}
      {(isPaused || isActivityPaused) && (
        <div className={styles.pausedOverlay}>
          <div className={styles.pausedContent}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#facc15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.pausedIcon}>
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
            <h2 className={styles.pausedTitle}>{isPaused ? 'Lesson Paused' : 'Activity Paused'}</h2>
            <p className={styles.pausedText}>Your instructor has paused the {isPaused ? 'lesson' : 'activity'}. Please wait.</p>
            {isActivityPaused && timeRemaining !== null && (
              <p className={styles.pausedText} style={{ marginTop: '0.5rem', fontSize: '1.5rem', fontFamily: 'monospace', fontWeight: 'bold' }}>
                {formatTime(timeRemaining)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
